import React, { useState, useEffect, useRef, useCallback } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import { isNativeApp } from '../../core/platform';
import { ClientHomeShimmer } from '../../mobile/ClientHomeShimmer';
import { INR, initials, today, uuid, invoiceStatus, dateInRange, logAudit, notify, getLatestSubmission, submissionStatusStyle, canClientSubmitTask, clientNeedsResubmit, clientPayable } from '../../core/helpers';
import { FilterBar } from '../../components/FilterBar';
import { InvoicesTable } from '../invoices/InvoicesPage';
import { useTableTools } from '../../core/tableView';
import { SortableTh } from '../../components/SortableTh';
import { LoadMoreBar } from '../../components/LoadMoreBar';
import { IconProperties } from '../../icons.properties';
import { Icon, InlineIcon } from '../../components/Icon';
import { DocActions } from '../../components/DocActions';
import { Badge } from '../../components/Badge';


function ClientHome() {
  const { user, tasks, invoices, myCompanies, submissions, openModal, paymentSchedule, dbReady } = useApp();
  const [selectedCo, setSelectedCo] = useState(null);

  if (!dbReady) return <ClientHomeShimmer />;

  // #12 — the companies linked to this person (link table + email/phone fallback)
  const displayClients = myCompanies;
  const reuploadTasks = tasks.filter(t => clientNeedsResubmit(submissions, t.id));

  if (selectedCo) return <ClientCompanyDetail company={selectedCo} onBack={()=>setSelectedCo(null)} />;

  return (
    <>
      {reuploadTasks.length > 0 && (
        <div style={{ marginBottom: 16, display: "flex", flexDirection: "column", gap: 10 }}>
          {reuploadTasks.map(t => {
            const sub = getLatestSubmission(submissions, t.id);
            return (
              <div key={t.id} className="card" style={{ border: "1px solid #FECACA", background: "#FEF2F2", padding: "14px 16px" }}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#DC2626", marginBottom: 4 }}>
                      <InlineIcon name="undo" size={13} gap={5}>Documents declined — reupload required</InlineIcon>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{t.title}</div>
                    <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }}>{t.clientName}</div>
                    {sub?.reviewNote && <div style={{ fontSize: 12, color: "#991B1B", marginTop: 6 }}>{sub.reviewNote}</div>}
                  </div>
                  <button className="btn btn-primary btn-sm" onClick={() => openModal("client-submit-task", { task: t })}>
                    Reupload →
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div style={{ marginBottom:16, display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
        <div style={{ fontSize:14,fontWeight:600,color:"var(--muted)", flex:1, minWidth:200 }}>
          Welcome back, <span style={{ color:"var(--ink)" }}>{user.name}</span>. Here are your companies.
        </div>
        <button className="btn btn-primary" onClick={()=>openModal("request-callback")}>
          <InlineIcon name="phoneCall" size={14} gap={6}>Request a Call Back</InlineIcon>
        </button>
      </div>
      {displayClients.length === 0 ? (
        <div className="card" style={{ padding: 24, textAlign: 'center' }}>
          <div style={{ fontFamily: "'Fraunces',serif", fontSize: 18, marginBottom: 8 }}>No companies linked yet</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, maxWidth: 420, margin: '0 auto' }}>
            Your login is active, but no company has been assigned to your account yet.
            Ask your account manager to link your portal login to your company in Founders Bridge.
          </div>
        </div>
      ) : (
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))", gap:16 }}>
        {displayClients.map(c => {
          const clientTasks = tasks.filter(t => t.clientId === c.id);
          const done        = clientTasks.filter(t=>t.status==="completed").length;
          const pending     = clientTasks.filter(t=>t.status!=="completed"&&t.status!=="cancelled").length;
          const needsAction = clientTasks.filter(t=>t.status==="client_action").length;
          const progress    = clientTasks.length ? Math.round(done/clientTasks.length*100) : c.progress||0;
          const clientInvs  = invoices.filter(i=>i.clientId===c.id && i.status!=="void");
          const outstanding = clientInvs.reduce((s, i) => {
            const p = clientPayable(i, paymentSchedule);
            return s + (p.hasInstallments ? p.amount : (i.pending || 0));
          }, 0);

          return (
            <div key={c.id} className="card" style={{ cursor:"pointer", position:"relative", overflow:"hidden", transition:"all .2s" }}
              onClick={()=>setSelectedCo(c)}
              onMouseEnter={e=>e.currentTarget.style.transform="translateY(-3px)"}
              onMouseLeave={e=>e.currentTarget.style.transform="translateY(0)"}
            >
              <div style={{ position:"absolute",top:0,left:0,right:0,height:3,background:"linear-gradient(90deg,var(--blue),var(--orange))" }}/>
              <div className="card-body">
                {/* Header */}
                <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10 }}>
                  <span className="tag">{c.type}</span>
                  <span style={{ fontSize:10,fontWeight:700,color:"var(--muted)",background:"#F3F4F6",padding:"2px 8px",borderRadius:5 }}>{c.clientNo}</span>
                </div>
                <div style={{ fontFamily:"'Fraunces',serif",fontSize:18,lineHeight:1.3,marginBottom:12 }}>{c.name}</div>

                {/* Action needed alert */}
                {needsAction > 0 && (
                  <div style={{ padding:"7px 10px",background:"#FFF7ED",border:"1px solid #FED7AA",borderRadius:7,marginBottom:12,fontSize:12,color:"#C2410C",fontWeight:600 }}>
                    <InlineIcon name="warning" size={13} gap={5}>{needsAction} task{needsAction>1?"s":""} need{needsAction===1?"s":""} your action</InlineIcon>
                  </div>
                )}

                {/* Progress */}
                <div style={{ marginBottom:12 }}>
                  <div style={{ display:"flex",justifyContent:"space-between",marginBottom:5 }}>
                    <span style={{ fontSize:12,color:"var(--muted)" }}>Overall Progress</span>
                    <span style={{ fontSize:12,fontWeight:700,color:progress===100?"var(--green)":"var(--blue)" }}>{progress}%</span>
                  </div>
                  <div className="prog-bg"><div className="prog-fill" style={{ width:progress+"%",background:progress===100?"var(--green)":"var(--blue)" }}/></div>
                </div>

                {/* Stats row */}
                <div style={{ display:"flex",gap:0,paddingTop:12,borderTop:"1px solid var(--border)" }}>
                  {[
                    ["Tasks Done",    `${done}/${clientTasks.length}`,  "var(--green)"],
                    ["Pending",       pending,                           "var(--orange)"],
                    ["Outstanding",   outstanding>0?INR(outstanding):"Nil", outstanding>0?"var(--red)":"var(--green)"],
                  ].map(([l,v,c],i)=>(
                    <div key={l} style={{ flex:1,textAlign:"center",borderRight:i<2?"1px solid var(--border)":undefined }}>
                      <div style={{ fontSize:15,fontWeight:700,color:c,marginBottom:2 }}>{v}</div>
                      <div style={{ fontSize:10,color:"var(--muted)" }}>{l}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      )}
    </>
  );
}

// ─── Client Company Detail Page ───────────────────────────────────────

function ClientCompanyDetail({ company, onBack }) {
  const { tasks, invoices, submissions, openModal, clients, showToast, paymentSchedule } = useApp();
  const [tab, setTab] = useState("profile");
  const [taskSearch, setTaskSearch] = useState("");
  const [taskSortKey, setTaskSortKey] = useState("dueDate");
  const [taskSortDir, setTaskSortDir] = useState("asc");
  const toggleTaskSort = (key) => {
    if (taskSortKey === key) setTaskSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setTaskSortKey(key); setTaskSortDir("asc"); }
  };
  const TASK_ACCESSORS = {
    sequence: (t) => Number(t.sequence) || 0,
    title: (t) => (t.title || '').toLowerCase(),
    status: (t) => t.status || '',
    dueDate: (t) => (t.dueDate ? new Date(t.dueDate).getTime() : 0),
  };

  const allCompanyTasks = tasks.filter(t => t.clientId === company.id);
  const clientTasksAll = allCompanyTasks
    .filter(t => !taskSearch || (t.title || "").toLowerCase().includes(taskSearch.toLowerCase()));
  const taskTable = useTableTools(clientTasksAll, {
    accessors: TASK_ACCESSORS,
    sortKey: taskSortKey,
    sortDir: taskSortDir,
    onSort: toggleTaskSort,
  });
  const clientTasks = taskTable.visible;
  const clientInvs  = invoices.filter(i => i.clientId === company.id);
  const liveInvs    = clientInvs.filter(i => i.status !== "void");
  const needsAction = allCompanyTasks.filter(t=>t.status==="client_action");
  const reuploadTasks = allCompanyTasks.filter(t => clientNeedsResubmit(submissions, t.id));
  const totalBilled = liveInvs.reduce((s,i)=>s+i.total,0);
  const totalPaid   = liveInvs.reduce((s,i)=>s+i.paid,0);
  const outstanding = liveInvs.reduce((s, i) => {
    const p = clientPayable(i, paymentSchedule);
    return s + (p.hasInstallments ? p.amount : (i.pending || 0));
  }, 0);
  const done        = allCompanyTasks.filter(t=>t.status==="completed").length;
  const progress    = allCompanyTasks.length ? Math.round(done/allCompanyTasks.length*100) : 0;

  // Documents submitted across all tasks
  const allDocs = [];
  submissions.filter(s=>allCompanyTasks.some(t=>t.id===s.taskId)).forEach(sub=>{
    if (sub.documents) {
      Object.entries(sub.documents).forEach(([dirIdx,slots])=>{
        Object.entries(slots).forEach(([slotId,doc])=>{
          allDocs.push({ ...doc, taskId:sub.taskId, dirIdx, submittedAt:sub.submittedAt });
        });
      });
    }
  });

  return (
    <>
      <button className="btn" style={{ marginBottom:16 }} onClick={onBack}><InlineIcon name="back" size={13} gap={5}>My Companies</InlineIcon></button>

      {/* Company header */}
      <div className="card" style={{ padding:"20px 24px",marginBottom:20 }}>
        <div style={{ display:"flex",alignItems:"flex-start",gap:16 }}>
          <div style={{ width:50,height:50,borderRadius:13,background:"linear-gradient(135deg,var(--navy),var(--blue))",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,flexShrink:0 }}><Icon name="company" size={24} color="#fff" /></div>
          <div style={{ flex:1 }}>
            <div style={{ display:"flex",alignItems:"center",gap:10,flexWrap:"wrap" }}>
              <span style={{ fontFamily:"'Fraunces',serif",fontSize:20 }}>{company.name}</span>
              <span className="tag">{company.type}</span>
            </div>
            <div style={{ display:"flex",gap:16,marginTop:8,flexWrap:"wrap",fontSize:12,color:"var(--muted)" }}>
              <span><InlineIcon name="clipboard" size={12} gap={4}>{company.clientNo}</InlineIcon></span>
              {company.contactName && <span>Contact: {company.contactName}</span>}
              {company.email && <span>Email: {company.email}</span>}
              {company.phone && <span>Phone: {company.phone}</span>}
              {company.cin  && <span>CIN: {company.cin}</span>}
              {company.gst  && <span>GST: {company.gst}</span>}
              {company.pan  && <span>PAN: {company.pan}</span>}
              {company.state && <span>State: {company.state}</span>}
              {company.incorporationDate && <span>Incorporated: {company.incorporationDate}</span>}
            </div>
          </div>
          <div style={{ textAlign:"right" }}>
            <div style={{ fontSize:11,color:"var(--muted)",marginBottom:4 }}>Overall Progress</div>
            <div style={{ fontSize:24,fontWeight:700,color:progress===100?"var(--green)":"var(--blue)" }}>{progress}%</div>
          </div>
        </div>
      </div>

      {reuploadTasks.length > 0 && (
        <div style={{ marginBottom: 16, display: "flex", flexDirection: "column", gap: 10 }}>
          {reuploadTasks.map(t => {
            const sub = getLatestSubmission(submissions, t.id);
            return (
              <div key={t.id} className="card" style={{ border: "1px solid #FECACA", background: "#FEF2F2", padding: "14px 16px" }}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#DC2626" }}>
                      <InlineIcon name="undo" size={13} gap={5}>Reupload required</InlineIcon>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 600, marginTop: 4 }}>{t.title}</div>
                    {sub?.reviewNote && <div style={{ fontSize: 12, color: "#991B1B", marginTop: 6 }}>{sub.reviewNote}</div>}
                  </div>
                  <button className="btn btn-primary btn-sm" onClick={() => openModal("client-submit-task", { task: t })}>Reupload →</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Action needed banner */}
      {needsAction.length > 0 && (
        <div style={{ padding:"14px 18px",background:"#FFF7ED",border:"1px solid #FED7AA",borderRadius:10,marginBottom:16,display:"flex",alignItems:"center",gap:12 }}>
          <Icon name="warning" size={22} color="#C2410C" />
          <div style={{ flex:1 }}>
            <div style={{ fontSize:14,fontWeight:700,color:"#C2410C" }}>{needsAction.length} task{needsAction.length>1?"s":""} need{needsAction.length===1?"s":""} your action</div>
            <div style={{ fontSize:12,color:"#92400E",marginTop:2 }}>Please complete and submit these to allow your team to proceed</div>
          </div>
          <button className="btn btn-sm" style={{ background:"#C2410C",color:"#fff",border:"none" }} onClick={()=>setTab("tasks")}>
            View Tasks →
          </button>
        </div>
      )}

      <div className="tabs">
        {[["profile","Profile"],["tasks","Tasks"],["billing","Billing"],["docs","Documents"]].map(([id,label])=>(
          <div key={id} className={`tab ${tab===id?"on":""}`} onClick={()=>setTab(id)}>{label}</div>
        ))}
      </div>

      {tab==="profile" && (
        <div className="card">
          <div className="card-head"><div className="card-title">Company Profile</div></div>
          <div className="card-body" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 16 }}>
            {[
              ["Company Name", company.name],
              ["Client No.", company.clientNo],
              ["Type", company.type],
              ["Contact Person", company.contactName || "—"],
              ["Email", company.email || "—"],
              ["Phone", company.phone || "—"],
              ["CIN / LLP", company.cin || "—"],
              ["GSTIN", company.gst || "—"],
              ["PAN", company.pan || "—"],
              ["State", company.state || "—"],
              ["Incorporation", company.incorporationDate || "—"],
              ["Status", company.status || "—"],
            ].map(([label, val]) => (
              <div key={label}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{val}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab==="tasks" && (
        <>
          <FilterBar search={taskSearch} setSearch={setTaskSearch} searchPlaceholder="Search tasks…" onClear={() => setTaskSearch("")} />
          <div className="card">
            <div className="tbl-wrap">
              <table>
                <thead><tr>
                  <SortableTh label="#" sortKey="sequence" activeKey={taskSortKey} sortDir={taskSortDir} onSort={toggleTaskSort} />
                  <SortableTh label="Task" sortKey="title" activeKey={taskSortKey} sortDir={taskSortDir} onSort={toggleTaskSort} />
                  <SortableTh label="Status" sortKey="status" activeKey={taskSortKey} sortDir={taskSortDir} onSort={toggleTaskSort} />
                  <SortableTh label="Due Date" sortKey="dueDate" activeKey={taskSortKey} sortDir={taskSortDir} onSort={toggleTaskSort} />
                  <th>Action</th>
                </tr></thead>
                <tbody>
                  {clientTasks.length===0 && (
                    <tr><td colSpan={5}><div className="empty"><div className="empty-icon"><Icon name="check" size={28} /></div><div>No tasks yet</div></div></td></tr>
                  )}
                  {clientTasks.map(t=>{
                    const sub = getLatestSubmission(submissions, t.id);
                    const docStatus = submissionStatusStyle(sub?.status);
                    const client = clients.find(c => c.id === t.clientId) || company;
                    const canSubmit = canClientSubmitTask(t, sub, client);
                    const isOverdue = t.dueDate&&new Date(t.dueDate)<new Date()&&t.status!=="completed";
                    return (
                      <tr key={t.id}>
                        <td style={{ fontSize:11,color:"var(--muted)",fontWeight:700 }}>#{t.sequence}</td>
                        <td>
                          <div style={{ fontWeight:500,fontSize:13 }}>{t.title}</div>
                          {sub?.status==="changes_requested"&&<div style={{ fontSize:11,color:"var(--red)",marginTop:2 }}><InlineIcon name="undo" size={11} gap={4}>Changes needed: {sub.reviewNote}</InlineIcon></div>}
                        </td>
                        <td>
                          <Badge status={t.status}/>
                          {docStatus && (
                            <div style={{ fontSize: 10, fontWeight: 600, color: docStatus.color, background: docStatus.bg, padding: "2px 6px", borderRadius: 4, marginTop: 4, display: "inline-block" }}>
                              {docStatus.label}
                            </div>
                          )}
                        </td>
                        <td>
                          <span style={{ fontSize:12,fontWeight:600,color:isOverdue?"var(--red)":"var(--muted)" }}>
                            {t.dueDate?new Date(t.dueDate).toLocaleDateString("en-IN",{day:"numeric",month:"short"}):"—"}
                            {isOverdue&&<> <Icon name="warning" size={12} color="var(--red)" /></>}
                          </span>
                        </td>
                        <td>
                          {canSubmit && t.status !== "team_approval" && t.status !== "completed" && (
                            <button className="btn btn-blue btn-sm" onClick={()=>openModal("client-submit-task",{task:t})}>
                              {sub?.status==="changes_requested"?"Resubmit":"Submit →"}
                            </button>
                          )}
                          {!canSubmit && t.status !== "completed" && t.status !== "team_approval" && (
                            <span style={{ fontSize:12,color:"var(--muted)" }}>Awaiting team</span>
                          )}
                          {t.status==="completed"&&<span style={{ fontSize:12,color:"var(--green)",fontWeight:700 }}><InlineIcon name="check" size={12} gap={4}>Done</InlineIcon></span>}
                          {t.status==="team_approval"&&<span style={{ fontSize:12,color:"var(--purple)" }}><InlineIcon name="hourglass" size={12} gap={4}>Reviewing</InlineIcon></span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <LoadMoreBar hasMore={taskTable.hasMore} loadingMore={false} onLoadMore={taskTable.loadMore} count={taskTable.shownCount} />
          </div>
        </>
      )}

      {tab==="billing" && (
        <>
          <div className="stat-grid" style={{ gridTemplateColumns:"repeat(3,1fr)",marginBottom:20 }}>
            {[["Total Billed",INR(totalBilled),"var(--ink)"],["Paid",INR(totalPaid),"var(--green)"],["Outstanding",INR(outstanding),outstanding>0?"var(--red)":"var(--muted)"]].map(([l,v,c])=>(
              <div key={l} className="stat-box"><div className="stat-lbl">{l}</div><div className="stat-val" style={{ fontSize:22,color:c }}>{v}</div></div>
            ))}
          </div>
          <InvoicesTable invoices={clientInvs} />
        </>
      )}

      {tab==="docs" && (
        <div className="card">
          <div className="card-head"><div className="card-title">Submitted Documents</div></div>
          {allDocs.length===0 ? (
            <div className="empty"><div className="empty-icon"><Icon name="folder" size={28} /></div><div>No documents submitted yet</div></div>
          ) : (
            allDocs.map((d,i)=>(
              <div key={i} className="row-item">
                <Icon name="doc" size={20} />
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13,fontWeight:500 }}>{d.name}</div>
                  <div style={{ fontSize:11,color:"var(--muted)" }}>{d.size} · Submitted {d.submittedAt}</div>
                </div>
                <DocActions doc={d} onError={(msg) => showToast(msg, "error")} />
              </div>
            ))
          )}
        </div>
      )}
    </>
  );
}


export { ClientHome, ClientCompanyDetail };
